import {
    ApplicationRef, ComponentRef, Injectable, Injector, OutputEmitterRef, RendererFactory2, TemplateRef, createComponent, inject, DOCUMENT
} from '@angular/core';

import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { HttpProgressEvent } from '@angular/common/http';
import { Observable } from 'rxjs';

import { BaseMin } from '@base/base-min';
import { Chat, Group, IVzMenuItem, Person, SM, SigninRequest, Task } from '@models';
import { PersonOrGroup, PersonsSelectComponent } from '@shared/_select/persons-select/persons-select.component';
import { ConfirmDangerComponent } from '@shared/_utils/confirm-danger/confirm-danger.component';
import { ChatsSelectComponent } from '@shared/_chats/chats-select/chats-select.component';
import { AvatarUploadComponent } from '@shared/_drawers/avatar-upload/avatar-upload.component';
import { TasksSelectComponent } from '@shared/_tasks/tasks-select';
import { ContextMenuComponent } from '@shared/_views/context-menu.component';
import { VzConfirmComponent } from '@shared/_utils/vz-confirm';

@Injectable()
export class HelpersService extends BaseMin {

    private readonly _drawer    = inject(NzDrawerService);
    private readonly _injector  = inject(Injector);
    private readonly _rf2       = inject(RendererFactory2);
    private readonly _doc       = inject(DOCUMENT);

    private readonly _r2 = this._rf2.createRenderer(null, null);

    selectPersons(opts: {
        persons?: Person[],
        pSelected?: string[],
        title?: string,
        groups?: Group[],
        gSelected?: string[],
        disabledIds?: SM<boolean>,
        okText?: string,
        cantBeEmpty?: boolean
    }): NzDrawerRef<PersonsSelectComponent, { ps: string[], gs: string[] } | undefined> {
        if (opts.title == null) {
            opts.title = 'Выберите людей из списка';
        }
        if (opts.okText == null) {
            opts.okText = 'Применить';
        }
        const width = this._mobile() ? '100%' : 550;
        return this._drawer.create<
            PersonsSelectComponent,
            { persons: Person[] | undefined, selected: string[] | undefined },
            { ps: string[], gs: string[] } | undefined
        >({
            nzTitle: opts.title,
            nzContent: PersonsSelectComponent,
            nzWidth: width,
            nzContentParams: {
                persons: opts.persons,
                pSelected: opts.pSelected,
                groups: opts.groups,
                gSelected: opts.gSelected,
                okText: opts.okText,
                disabledIds: opts.disabledIds,
                cantBeEmpty: opts.cantBeEmpty
            }
        });
    }

    selectPersonOrGroup(
        persons?: Person[],
        groups?: Group[],
        title: string = 'Выберите человека или группу из списка',
    ): NzDrawerRef<PersonsSelectComponent, PersonOrGroup | undefined> {
        const width = this._mobile() ? '100%' : 600;
        return this._drawer.create<
            PersonsSelectComponent,
            { persons: Person[] | undefined, selected: string | undefined },
            PersonOrGroup | undefined
        >({
            nzTitle: title,
            nzContent: PersonsSelectComponent,
            nzWidth: width,
            nzContentParams: { persons, groups, single: true, returnObject: true }
        });
    }

    selectPerson(
        persons?: Person[],
        selected?: string,
        title: string = 'Выберите человека из списка',
    ): NzDrawerRef<PersonsSelectComponent, string | undefined> {
        const width = this._mobile() ? '100%' : 600;
        return this._drawer.create<
            PersonsSelectComponent,
            { persons: Person[] | undefined, selected: string | undefined },
            string | undefined
        >({
            nzTitle: title,
            nzContent: PersonsSelectComponent,
            nzWidth: width,
            nzContentParams: { persons, selected, single: true }
        });
    }

    selectGroup(
        groups?: Group[],
        selected?: string,
        title: string = 'Выберите группу из списка',
    ): NzDrawerRef<PersonsSelectComponent, string | undefined> {
        const width = this._mobile() ? '100%' : 600;
        return this._drawer.create<
            PersonsSelectComponent,
            { groups: Group[] | undefined, selected: string | undefined },
            string | undefined
        >({
            nzTitle: title,
            nzContent: PersonsSelectComponent,
            nzWidth: width,
            nzContentParams: { groups, selected, single: true }
        });
    }

    confirm(opts: {
        ok: () => void,
        cancel?: () => void,
        okText?: string,
        okDanger?: boolean,
        cancelText?: string,
        title?: string,
        content?: string,
        maxWidth?: string,
        backdropCancel?: boolean,
        showBackdrop?: boolean,
    } | (() => void)): void {
        if (typeof opts == 'function') {
            opts = { ok: opts };
        }

        const host = this._r2.createElement('div');
        this._r2.setAttribute(host, 'id', 'vzConfirm');

        const appRef = this._injector.get(ApplicationRef);
        const cmpRef = createComponent(VzConfirmComponent, { environmentInjector: appRef.injector, hostElement: host });
        cmpRef.setInput('title', opts.title || 'Вы уверены?');
        if (opts.content != null) {
            cmpRef.setInput('content', opts.content);
        }
        cmpRef.setInput('okText', opts.okText || 'ОК');
        cmpRef.setInput('cancelText', opts.cancelText || 'Отмена');
        if (opts.okDanger != null) {
            cmpRef.setInput('okDanger', opts.okDanger);
        }
        if (opts.maxWidth != null) {
            cmpRef.setInput('maxWidth', opts.maxWidth);
        }
        if (opts.backdropCancel != null) {
            cmpRef.setInput('backdropCancel', opts.backdropCancel);
        }
        if (opts.showBackdrop != null) {
            cmpRef.setInput('showBackdrop', opts.showBackdrop);
        }
        cmpRef.instance.okClick.subscribe(() => {
            appRef.detachView(cmpRef.hostView);
            cmpRef.destroy();
            opts.ok?.();
        });
        cmpRef.instance.cancelClick.subscribe(() => {
            appRef.detachView(cmpRef.hostView);
            cmpRef.destroy();
            opts.cancel?.();
        });
        cmpRef.changeDetectorRef.detectChanges();
        appRef.attachView(cmpRef.hostView);

        this._r2.appendChild(this._doc.body, host);

    }

    confirmDanger(
        title: string = 'Внимание!!!',
        text?: string
    ): NzDrawerRef<ConfirmDangerComponent, SigninRequest | undefined> {
        const width = this._mobile() ? '100%' : 450;
        return this._drawer.create<
            ConfirmDangerComponent,
            any,
            SigninRequest | undefined
        >({
            nzTitle: title,
            nzContent: ConfirmDangerComponent,
            nzWidth: width,
            nzPlacement: 'left',
            nzContentParams: { text },
            nzWrapClassName: 'ant-drawer-danger',
        });
    }

    selectTasks(opts?: {
        multiple?: boolean,
        tasks?: Task[],
        selected?: string[],
        title?: string,
        projectId?: string,
        filterFn?: (t: Task) => boolean,
        excludeTaskId?: string,
    }): NzDrawerRef<TasksSelectComponent, Task[] | undefined> {
        const width = this._mobile() ? '100%' : 900;
        return this._drawer.create<
            TasksSelectComponent,
            { projectId?: string, excludeTaskId?: string, filterFn?: (t: Task) => boolean, tasks?: Task[], selected?: string[], multiple?: boolean },
            Task[] | undefined
        >({
            nzTitle: opts?.multiple ? 'Выберите задачи' : 'Выберите задачу',
            nzContent: TasksSelectComponent,
            nzWidth: width,
            nzContentParams: {
                projectId: opts?.projectId,
                excludeTaskId: opts?.excludeTaskId,
                filterFn: opts?.filterFn,
                tasks: opts?.tasks,
                selected: opts?.selected,
                multiple: opts?.multiple
            }
        });
    }

    // selectChats(opts: {
    //     chatsFilter?: (c: Chat) => boolean,
    //     selected?: string[],
    //     single?: boolean,
    //     noFilters?: boolean,
    //     returnObjects?: boolean,
    //     title?: string,
    //     okText?: string,
    //     avatarSize?: number
    // }): NzDrawerRef<ChatsSelectComponent, Chat[] | string[] | undefined> {
    //     const width = this._mobile() ? '100%' : 550;
    //     const dr = this._drawer.create<
    //         ChatsSelectComponent,
    //         { items: Chat[] | undefined, selected: string[] | undefined },
    //         Chat[] | string[] | undefined
    //     >({
    //         nzTitle: opts.title || 'Выберите чаты из списка',
    //         nzContent: ChatsSelectComponent,
    //         nzWidth: width,
    //         // nzContentParams: {
    //         //     chatsFilter: opts.chatsFilter,
    //         //     selected: opts.selected,
    //         //     single: opts.single,
    //         //     returnObjects: opts.returnObjects,
    //         //     okText: opts.okText,
    //         //     noFilters: opts.noFilters,
    //         //     avatarSize: opts.avatarSize
    //         // }
    //     });
    //     const cr = dr.getContentComponentRef();
    //     cr?.setInput('chatsFilter', opts.chatsFilter);
    //     cr?.setInput('selected', opts.selected);
    //     cr?.setInput('single', opts.single);
    //     cr?.setInput('returnObjects', opts.returnObjects);
    //     cr?.setInput('okText', opts.okText);
    //     cr?.setInput('noFilters', opts.noFilters);
    //     cr?.setInput('avatarSize', opts.avatarSize);
    //     cr?.changeDetectorRef.detectChanges();
    //     return dr;
    // }

    selectChats(opts: {
        chatsFilter?: (c: Chat) => boolean,
        selected?: string[],
        single?: boolean,
        noFilters?: boolean,
        title?: string,
        okText?: string,
        avatarSize?: number
    }): {
        cmpRef: ComponentRef<ChatsSelectComponent>,
        close: OutputEmitterRef<Chat[] | undefined>
    } {
        const host = this._r2.createElement('div');
        this._r2.setAttribute(host, 'id', 'vzDrawer');

        const appRef = this._injector.get(ApplicationRef);
        const cmpRef = createComponent(ChatsSelectComponent, { environmentInjector: appRef.injector, hostElement: host });
        cmpRef.setInput('chatsFilter', opts.chatsFilter);
        cmpRef.setInput('selected', opts.selected);
        cmpRef.setInput('single', opts.single);
        cmpRef.setInput('okText', opts.okText);
        cmpRef.setInput('noFilters', opts.noFilters);
        cmpRef.setInput('avatarSize', opts.avatarSize);
        cmpRef.instance.onClose.subscribe(() => {
            appRef.detachView(cmpRef.hostView);
            cmpRef.destroy();
        });
        cmpRef.changeDetectorRef.detectChanges();
        appRef.attachView(cmpRef.hostView);
        this._r2.appendChild(this._doc.body, host);
        console.log('-[selectChats]--------------------> cmpRef:', cmpRef, 'hostView:', cmpRef.hostView);
        return { cmpRef, close: cmpRef.instance.onClose };
    }

    uploadAvatar(uploadFn: (file: File, eventHandler: (e: HttpProgressEvent) => void) => Observable<void>): NzDrawerRef<AvatarUploadComponent, void> {
        const width = this._mobile() ? '100%' : 450;
        return this._drawer.create<AvatarUploadComponent, { uploadFn: (file: File, eventHandler: (e: HttpProgressEvent) => void) => Observable<void>, avatarId: string, avatarShape: string }, void>({
            nzTitle: 'Загрузить новый аватар',
            nzContent: AvatarUploadComponent,
            nzWidth: width,
            nzContentParams: { uploadFn }
        });
    }

    showContextMenu(opts: {
        event?: MouseEvent | { x: number, y: number },
        single?: IVzMenuItem,
        items?: IVzMenuItem[],
        title?: string | TemplateRef<any>,
        drawerHeight?: number,
        data?: any
    }): {
        cmpRef: ComponentRef<ContextMenuComponent>,
        click: OutputEmitterRef<{ mi: IVzMenuItem, data: any, res?: any }>,
        close: OutputEmitterRef<void>
    } {
        const appRef = this._injector.get(ApplicationRef);
        const cmpRef = createComponent(ContextMenuComponent, { environmentInjector: appRef.injector });
        // console.log('=[HelpersService]===> showContextMenu', opts);
        cmpRef.setInput('singleItem', opts.single);
        cmpRef.setInput('menuItems', opts.items);
        cmpRef.setInput('menuTitle', opts.title);
        cmpRef.setInput('event', opts.event);
        cmpRef.setInput('data', opts.data);
        cmpRef.setInput('drawerHeight', opts.drawerHeight);
        cmpRef.setInput('mobile', this._mobile());
        cmpRef.instance.onClose.subscribe(() => {
            appRef.detachView(cmpRef.hostView);
            cmpRef.destroy();
        });
        cmpRef.changeDetectorRef.detectChanges();
        appRef.attachView(cmpRef.hostView);
        return { cmpRef, click: cmpRef.instance.onItemClick, close: cmpRef.instance.onClose };
    }

}
